import { ThunkApiConfig } from "RootType"

import { resetAnalytics } from "../../analytics"
import {
  invalidateAuthToken,
  loginURL,
  oAuthAuthorizeURL,
  oAuthCallbackURL,
  postForm,
  postJSON,
  requestLoginURL,
  tabletAuthenticateURL,
  tokenURL,
} from "../../api"
import { ResponseError } from "../../api/apiUtils"
import { LANGUAGE_STORAGE_KEY } from "../../i18n"
import { getErrorObject } from "../reduxUtils"
import { AuthenticateTabletRequest } from "../tablet/types"
import { AuthResponse } from "./types"
import { ONBOARDING_STORAGE_KEY } from "@mobile/screens/Home/components/OnboardingTour/constants"
import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from "@reduxjs/toolkit"

const CLIENT_ID = import.meta.env.VITE_APP_OAUTH_CLIENT_ID
const CLIENT_SECRET = import.meta.env.VITE_APP_OAUTH_CLIENT_SECRET

const PWA_CLIENT_ID = import.meta.env.VITE_APP_OAUTH_PWA_CLIENT_ID
const PWA_CLIENT_SECRET = import.meta.env.VITE_APP_OAUTH_PWA_CLIENT_SECRET

export const ACCESS_TOKEN_KEY = "access_token"
export const REFRESH_TOKEN_KEY = "refresh_token"

/**
 * JOAN-10083: keys that survive `clearAuth`'s sweep of localStorage.
 * `LANGUAGE_STORAGE_KEY` keeps the user's chosen UI language across
 * logouts / 401s, and `force_device_render` keeps the manual app-mode
 * pick (Visitor Management vs Joan app) — both were silently lost
 * before the fix because `clearAuth` called `localStorage.clear()`.
 * `ONBOARDING_STORAGE_KEY` keeps the new mobile app's Joyride tour from
 * replaying every time the user logs out and back in on the same device.
 */
const PRESERVED_LOCAL_STORAGE_KEYS = [
  LANGUAGE_STORAGE_KEY,
  "force_device_render",
  ONBOARDING_STORAGE_KEY,
] as const

/**
 *  Thunks
 */
export const fetchAuthToken = createAsyncThunk<
  AuthResponse,
  void,
  ThunkApiConfig
>("auth/fetchAuthToken", async (_, { getState, rejectWithValue }) => {
  const {
    auth: { code },
  } = getState()

  const body = {
    grant_type: "authorization_code",
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
    code: code,
    redirect_uri: oAuthCallbackURL(),
  }

  const response = await postForm(tokenURL(), { body })
  const json = await response.json()

  if (response.ok) {
    return json
  }

  return rejectWithValue({ error: true, payload: json })
})

export const authenticateTablet = createAsyncThunk<
  AuthResponse,
  AuthenticateTabletRequest,
  ThunkApiConfig
>("auth/authenticateTablet", async (body, { rejectWithValue }) => {
  const response = await postJSON(tabletAuthenticateURL(), {
    body,
  })

  const json = await response.json()

  if (response.ok) {
    return json
  }

  return rejectWithValue({ error: true, payload: json })
})

export const requestLogin = createAsyncThunk<
  string,
  string,
  ThunkApiConfig<ResponseError>
>("auth/requestLogin", async (email, { rejectWithValue }) => {
  const body = {
    email: email,
  }

  const response = await postForm(requestLoginURL(), { body })

  if (response.ok) {
    return email
  }

  return rejectWithValue(await getErrorObject(response))
})

export const postLogin = createAsyncThunk<AuthResponse, string, ThunkApiConfig>(
  "auth/postLogin",
  async (password, { getState, rejectWithValue }) => {
    const {
      auth: { email },
    } = getState()

    const body = {
      grant_type: "password",
      client_id: PWA_CLIENT_ID,
      client_secret: PWA_CLIENT_SECRET,
      username: email,
      password: password,
    }

    const response = await postForm(loginURL(), { body })
    const json = await response.json()

    if (response.ok) {
      return json
    }

    return rejectWithValue({ error: true, payload: json })
  },
)

/**
 * Invalidates the OAuth token on the server when both tokens are present,
 * then clears the local auth state via `clearAuth`. When tokens are missing
 * locally we skip the network call and just clear local auth state.
 */
export const logout = createAsyncThunk<void, void, ThunkApiConfig>(
  "auth/logout",
  async (_, { getState, rejectWithValue, dispatch }) => {
    const {
      auth: { access_token, refresh_token },
    } = getState()

    if (access_token && refresh_token) {
      const response = await postJSON(
        invalidateAuthToken(),
        { body: {} },
        access_token,
      )

      if (response.ok) {
        dispatch(clearAuth())
        resetAnalytics()
        return
      }

      return rejectWithValue({ error: true })
    }

    dispatch(clearAuth())

    return
  },
)

export const fetchAuthCode = () => () => {
  window.location.href = oAuthAuthorizeURL()
}

/**
 *  Slice
 */
export type AuthReducerState = {
  email: string | null
  code: string | null
  access_token: string | null
  refresh_token: string | null
  error: boolean | string | null
  isLogin: boolean
}

const initialState: AuthReducerState = {
  email: null,
  code: null,
  access_token: localStorage.getItem(ACCESS_TOKEN_KEY),
  refresh_token: localStorage.getItem(REFRESH_TOKEN_KEY),
  error: false,
  isLogin: false,
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuth: (state) => {
      /**
       * JOAN-10083: clear the bulk of localStorage on logout / 401 (so
       * stale tablet pairing, filters, etc. don't leak across sessions),
       * but explicitly preserve user-facing preferences that are not
       * tied to the auth session — currently `app_language` and
       * `force_device_render`. Both were being silently wiped by the
       * previous `localStorage.clear()`, which made tablets revert to
       * Visitor Management on every cold start after a 401.
       */
      if (localStorage) {
        const preserved = PRESERVED_LOCAL_STORAGE_KEYS.map(
          (key) => [key, localStorage.getItem(key)] as const,
        )
        localStorage.clear()
        preserved.forEach(([key, value]) => {
          if (value !== null) {
            localStorage.setItem(key, value)
          }
        })
      }

      state.access_token = null
      state.refresh_token = null
    },
    setIsLogin: (state, action: PayloadAction<boolean>) => {
      const { payload } = action

      state.isLogin = payload
    },
    fetchEmailSuccess: (state, action: PayloadAction<string>) => {
      const { payload } = action

      state.email = payload
    },
    fetchAuthCodeSuccess: (state, action: PayloadAction<string>) => {
      const { payload } = action

      state.code = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAuthToken.rejected, (state) => {
      state.code = null
      state.error = true
    })
    builder.addCase(authenticateTablet.rejected, (state) => {
      state.code = null
      state.error = true
    })
    builder.addCase(requestLogin.rejected, (state) => {
      state.email = null
      state.error = true
    })
    builder.addCase(requestLogin.fulfilled, (state, action) => {
      const { payload } = action

      state.email = payload
      state.error = false
    })
    builder.addCase(postLogin.rejected, (state) => {
      state.error = true
    })
    builder.addMatcher(
      isAnyOf(
        fetchAuthToken.fulfilled,
        postLogin.fulfilled,
        authenticateTablet.fulfilled,
      ),
      (state, action) => {
        const { payload } = action

        const { access_token, refresh_token } = payload

        if (!access_token || !refresh_token) {
          return
        }

        if (localStorage) {
          localStorage.setItem(ACCESS_TOKEN_KEY, access_token)
          localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token)
        }

        state.access_token = access_token
        state.refresh_token = refresh_token
        state.code = null
        state.error = false
        state.isLogin = true
      },
    )
  },
})

export const authReducer = authSlice.reducer
export const {
  clearAuth,
  setIsLogin,
  fetchEmailSuccess,
  fetchAuthCodeSuccess,
} = authSlice.actions
